import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Skeleton, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import { Table, usePagination } from '@frontend/features/common/components/Table';
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { CreateTeamModal } from '@frontend/features/teams/components/CreateTeamModal';
import { useGetTeamsByAgency } from '@frontend/features/teams/queries';
import { EditTeamModal } from '@frontend/features/teams/components/EditTeamModal';
import { Link } from '@frontend/features/common/components/Link';
import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { TABLE_HEIGHT_WITH_AGENCIES_LAYOUT } from '@frontend/features/agencies/constants';

import { routes } from '../routes';

export const TeamsList = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const theme = useTheme();
  const pagination = usePagination();
  const [search, setSearch] = useState('');
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [editId, setEditId] = useState<string | undefined>();
  const [isOpenEdit, setIsOpenEdit] = useState<boolean>(false);

  const toggleCreateModal = () => setIsOpenCreate((prevState) => !prevState);
  const handleOpenEditModal = (id: string) => {
    setEditId(id);
    setIsOpenEdit(true);
  };

  const handleCloseEditModal = () => {
    setEditId(undefined);
    setIsOpenEdit(false);
  };

  const { data, isLoading } = useGetTeamsByAgency({
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
    search: search,
    agencyId: id,
  });

  const handleOnSearch = (value: string) => {
    setSearch(value);
  };

  return (
    <ListLayout padding={0} title={t('teams.title')} subtitle={t('teams.subtitle')}>
      <Box display="flex" alignItems="center" gap={theme.spacing(3)} marginBottom={theme.spacing(3)}>
        <Typography
          variant="subtitle1"
          align="left"
          color={theme.palette.text.primary}
          sx={{
            flexGrow: 1,
          }}
        >
          {data?.count} {t('teams.results')}
        </Typography>

        <SearchInput placeholder="Buscar" onSearch={handleOnSearch} />
        <ToggleButtonGroup
          exclusive
          sx={{
            height: theme.spacing(5),
            '& button': {
              padding: theme.spacing(1),
              width: theme.spacing(5),
            },
          }}
        >
          <ToggleButton onClick={toggleCreateModal} value="add" aria-label="right aligned">
            <AddIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {id && <CreateTeamModal agencyId={id} open={isOpenCreate} onClose={toggleCreateModal} />}
      {id && editId && (
        <EditTeamModal agencyId={id} open={isOpenEdit} id={Number(editId)} onClose={handleCloseEditModal} />
      )}

      {data?.count !== undefined && data.count > 0 && (
        <Table
          height={TABLE_HEIGHT_WITH_AGENCIES_LAYOUT}
          columns={[
            {
              id: 'color',
              label: t('teams.table.color'),
              align: 'left',
              format: (value) => (
                <Box
                  component="div"
                  sx={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: value,
                  }}
                >
                  {' '}
                </Box>
              ),
            },
            {
              id: 'name',
              label: t('teams.table.name'),
              align: 'left',
            },
            {
              id: 'agencyName',
              label: t('teams.table.agency'),
              align: 'left',
            },
            { id: 'actions', label: '', align: 'left' },
          ]}
          data={{
            key: 'id',
            items: data.items.map((item) => ({
              ...item,
              name: (
                <Link
                  to={`${routes.dashboard.agencyView.root.replace(
                    ':id',
                    String(id),
                  )}/${routes.dashboard.agencyView.team.replace(':teamId', String(item.id))}`}
                >
                  {item.name}
                </Link>
              ),
              agencyName: (
                <Link to={routes.dashboard.agencyView.root.replace(':id', String(item.agencyId))}>
                  {item.agencyName}
                </Link>
              ),
              actions: (
                <IconButton onClick={() => handleOpenEditModal(String(item.id))}>
                  <EditIcon color="primary" />
                </IconButton>
              ),
            })),
            totalCount: data.count,
          }}
          pagination={pagination}
          stickyHeader
        />
      )}

      {isLoading && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}
    </ListLayout>
  );
};
